import { useEffect, useMemo, useState } from "react";
import { ModalWrapper } from "@/components/modals/ModalWrapper";
import { financeService } from "../../../services/financeService";
import { useFinanceStore } from "../../../store/financeStore";
import type { MetaCampaign } from "./types";

interface MatchCampaignsModalProps {
  open: boolean;
  onClose: () => void;
  funnelId: string;
  funnelName: string;
}

const formatSpend = (value: number) =>
  `$${new Intl.NumberFormat("es-CO", { maximumFractionDigits: 0 }).format(value)}`;

const formatRoas = (value: number | null) => (value === null ? "-" : `${value.toFixed(1)}x`);

export const MatchCampaignsModal = ({
  open,
  onClose,
  funnelId,
  funnelName,
}: MatchCampaignsModalProps) => {
  const [search, setSearch] = useState("");
  const [campaigns, setCampaigns] = useState<MetaCampaign[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const funnel = useFinanceStore((state) => state.funnels.find((item) => item.id === funnelId));
  const updateFunnelMatch = useFinanceStore((state) => state.updateFunnelMatch);

  useEffect(() => {
    if (!open) return;

    setSearch("");
    setSelectedIds(funnel?.matchedCampaignIds ?? []);
    setLoadError(null);

    financeService.getMetaCampaigns()
      .then((items) => {
        setCampaigns(items);
      })
      .catch(() => {
        setCampaigns([]);
        setLoadError("No pudimos cargar tus campañas de Meta.");
      });
  }, [funnel?.matchedCampaignIds, open]);

  const filteredCampaigns = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) return campaigns;
    return campaigns.filter((campaign) => campaign.name.toLowerCase().includes(normalizedSearch));
  }, [campaigns, search]);

  const toggleCampaign = (campaignId: string) => {
    setSelectedIds((current) =>
      current.includes(campaignId)
        ? current.filter((id) => id !== campaignId)
        : [...current, campaignId]
    );
  };

  const handleSave = async () => {
    if (!selectedIds.length || isSaving) return;

    setIsSaving(true);
    try {
      await financeService.matchCampaignsToFunnel(funnelId, selectedIds);
      updateFunnelMatch(funnelId, selectedIds);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalWrapper isOpen={open} onClose={onClose}>
      <div className="p-6">
        <h2 className="mb-2 pr-8 text-lg font-semibold text-gray-900">
          Conectar campañas Meta a {funnelName}
        </h2>
        <p className="mb-4 text-sm leading-5 text-gray-500">
          Selecciona qué campañas de Meta corresponden a este embudo. Podrás cambiar la selección en cualquier momento.
        </p>

        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Buscar campaña..."
          className="mb-4 h-10 w-full rounded-lg border border-gray-200 px-3 text-sm outline-none focus:border-[#2b5ce6] focus:ring-2 focus:ring-[#2b5ce6]/10"
        />

        {loadError ? (
          <div className="mb-4 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
            {loadError}
          </div>
        ) : null}

        <div className="max-h-80 overflow-y-auto border-y border-gray-100">
          {filteredCampaigns.map((campaign) => {
            const selected = selectedIds.includes(campaign.id);

            return (
              <button
                type="button"
                key={campaign.id}
                onClick={() => toggleCampaign(campaign.id)}
                className="flex w-full items-start gap-3 border-b border-gray-100 px-1 py-3 text-left last:border-b-0 hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => toggleCampaign(campaign.id)}
                  onClick={(event) => event.stopPropagation()}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-[#2b5ce6] focus:ring-[#2b5ce6]"
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-gray-900">{campaign.name}</span>
                  <span className="mt-1 block text-xs text-gray-500">
                    Status: {campaign.status} · Inversión: {formatSpend(campaign.spend)} · ROAS: {formatRoas(campaign.roas)}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 items-center justify-center rounded-lg border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!selectedIds.length || isSaving}
            className="inline-flex h-10 items-center justify-center rounded-lg bg-[#2b5ce6] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[#214ec7] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSaving ? "Guardando..." : "Guardar match"}
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};
